import { useEffect, useRef, useState } from 'react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { GatedButton } from '../ui/GatedButton'
import { TextArea } from '../ui/Input'
import { useToast } from '../../contexts/ToastContext'
import { useSendParentNote } from '../../hooks/useFriends'
import { useActionBoundary } from '../../hooks/useActionBoundary'
import { apiError } from '../../lib/api/client'

interface ParentNoteDialogProps {
  open: boolean
  onClose: () => void
  toFamilyId: string
  toFamilyName?: string
}

/** Parent-to-parent note compose (COTC_FriendsApprovals R3). */
export function ParentNoteDialog({ open, onClose, toFamilyId, toFamilyName }: ParentNoteDialogProps) {
  const { flash } = useToast()
  const send = useSendParentNote()
  const captureBoundary = useActionBoundary(`${toFamilyId}:${open}`)
  const inFlight = useRef(false)
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { if (open) { setText(''); setError(null) } }, [open, toFamilyId])
  if (!open) return null

  async function submit() {
    if (inFlight.current || !text.trim()) return
    inFlight.current = true
    const current = captureBoundary()
    setError(null)
    try {
      await send.mutateAsync({ toFamilyId, text: text.trim() })
      if (!current()) return
      flash('Note sent to their parent.')
      onClose()
    } catch (err) {
      if (!current()) return
      const message = apiError(err).message
      setError(message === 'content_held' || message.startsWith('Held for review')
        ? 'Your note was not sent. Remove phone numbers, school details and off-site handles, then try again.'
        : message)
    } finally {
      inFlight.current = false
    }
  }

  return (
    <Modal open={open} onClose={() => { if (!inFlight.current) onClose() }} title={toFamilyName ? `Message ${toFamilyName}` : 'Message their parent'}>
      <p className="text-[13px] leading-relaxed text-ink-muted">
        A short note, parent to parent. Notes containing phone numbers, schools or off-site handles are not sent. Remove those details before sending.
      </p>
      <TextArea
        disabled={send.isPending}
        label="Your note"
        error={error ?? undefined}
        className="mt-3"
        rows={4}
        maxLength={400}
        placeholder="Say hello, sort out a time to play, or raise something gently."
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="mt-5 flex justify-between">
        <Button variant="outline" disabled={send.isPending} onClick={() => { if (!inFlight.current) onClose() }}>Cancel</Button>
        <GatedButton disabled={send.isPending} gated={text.trim().length === 0 || send.isPending} hint="Write a short note first." variant="ink" onClick={submit}>
          Send note
        </GatedButton>
      </div>
    </Modal>
  )
}
